import { useState, useCallback } from 'react';

import { Select, SelectChangeEvent, MenuItem } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import {
  getGetApiV5FirmsRequestsIdQueryKey,
  usePutApiV5FirmsRequestsId,
} from '@liscio/api';
import { ConfirmationDialog, InputLabel, Stack, Typography } from '@liscio/ui';

import {
  useTaxDeliveryReminderType,
  REMINDER_TYPE_OPTIONS,
  type ReminderType,
} from 'modules/requests/hooks/useTaxDeliverySettings';

type PaymentReminderSettingsProps = {
  requestId: string;
  currentReminderType: ReminderType;
  disabled?: boolean;
};

export function PaymentReminderSettings({
  requestId,
  currentReminderType,
  disabled = false,
}: PaymentReminderSettingsProps) {
  const queryClient = useQueryClient();
  const { updateReminderType: updateFirmDefault } =
    useTaxDeliveryReminderType();
  const { mutateAsync: updateRequest } = usePutApiV5FirmsRequestsId();

  const [localReminderType, setLocalReminderType] =
    useState<ReminderType>(currentReminderType);
  const [pendingValue, setPendingValue] = useState<ReminderType | null>(null);

  const handleReminderTypeChange = useCallback(
    (event: SelectChangeEvent) => {
      const newValue = event.target.value as ReminderType;
      setPendingValue(newValue);
    },
    []
  );

  const updateRequestReminderType = useCallback(
    async (value: ReminderType) => {
      try {
        await updateRequest(
          {
            id: requestId,
            data: { reminder_type: value },
          },
          {
            onSuccess: () => {
              queryClient.invalidateQueries(
                getGetApiV5FirmsRequestsIdQueryKey(requestId)
              );
            },
          }
        );
        setLocalReminderType(value);
      } catch {
        toast.error('Failed to update reminder type for this request.');
      }
    },
    [requestId, updateRequest, queryClient]
  );

  const handleSetAsDefault = async () => {
    if (!pendingValue) return;
    await updateRequestReminderType(pendingValue);
    updateFirmDefault(pendingValue);
    setPendingValue(null);
  };

  const handleOnlyThisRequest = async () => {
    if (!pendingValue) return;
    await updateRequestReminderType(pendingValue);
    setPendingValue(null);
  };

  const selectedReminder = REMINDER_TYPE_OPTIONS.find(
    (opt) => opt.value === localReminderType
  );

  const pendingLabel = REMINDER_TYPE_OPTIONS.find(
    (o) => o.value === pendingValue
  )?.label;

  return (
    <>
      <Stack gap="4px">
        <InputLabel sx={{ marginTop: 2 }}>ESTIMATED TAX PAYMENT REMINDERS</InputLabel>
        <Select
          value={localReminderType}
          onChange={handleReminderTypeChange}
          fullWidth
          size="small"
          disabled={disabled}
          aria-label="Estimated tax payment reminder type"
        >
          {REMINDER_TYPE_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
        {selectedReminder && (
          <Typography variant="caption" color="text.secondary">
            {selectedReminder.description}
          </Typography>
        )}
      </Stack>

      <ConfirmationDialog
        open={!!pendingValue}
        title="Update Reminder Setting"
        content={`Do you also want to set "${pendingLabel}" as the default for all future tax deliveries?`}
        confirmLabel="Set as Default"
        cancelLabel="Only This Request"
        onConfirm={handleSetAsDefault}
        onCancel={handleOnlyThisRequest}
      />
    </>
  );
}

export default PaymentReminderSettings;
